import Snackbar from '@polestar/component-warehouse-react/components/snackbar/Snackbar';
import generateId from '@polestar/component-warehouse-react/helpers/generateId';
import styled from '@emotion/styled';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { createContext, useCallback, useContext, useState, } from 'react';
import { useTimeout } from '@polestar/component-warehouse-react/components/snackbar/Snackbar.helpers';
import { AnimatePresence, m as motion } from 'framer-motion';
import { createTransition, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
const noProviderFunction = (handleName) => () => {
    console.warn(`Trying to use snackbar context consumer, but no context provider for "${handleName}". Please make sure you use the useSnackbar inside a <PolestarApp /> or check if there are multiple @polestar/component-warehouse-react versions installed due to (for example) shared modules hard dependencies.`);
};
const SnackbarContext = createContext({
    snackbars: [],
    addSnackbar: noProviderFunction('addSnackbar'),
    removeSnackbar: noProviderFunction('removeSnackbar'),
});
export const useSnackbar = () => useContext(SnackbarContext);
/**
 * @internal
 */
export const SnackbarProvider = ({ children }) => {
    const [snackbars, setSnackbars] = useState([]);
    const addSnackbar = useCallback(({ id = generateId(), duration = 7000, ...snackbar }) => {
        setSnackbars((oldSnackbars) => [
            ...oldSnackbars,
            {
                id,
                duration,
                ...snackbar,
            },
        ]);
        return id;
    }, []);
    const removeSnackbar = useCallback((id) => {
        setSnackbars((oldSnackbars) => oldSnackbars.filter((snackbar) => snackbar.id !== id));
    }, []);
    return (React.createElement(SnackbarContext.Provider, { value: {
            snackbars,
            addSnackbar,
            removeSnackbar,
        } },
        children,
        React.createElement(Portal, { zIndex: LayoutIndexes.modal + 1 },
            React.createElement(SnackbarRegion, null))));
};
const SnackbarRegion = () => {
    const { snackbars } = useSnackbar();
    return (React.createElement(SnackbarWrapper, null, snackbars.map(({ id, duration, closeOnClick, ...item }) => (React.createElement(SnackbarAnimationWrapper, { id: id, key: `snack-${id}`, duration: duration, closeOnClick: closeOnClick, item: item })))));
};
/**
 * @internal
 */
export default SnackbarRegion;
const SnackbarWrapper = styled.div `
  pointer-events: none;
  width: 100%;
  max-width: 480px;
  min-width: 280px;
  position: fixed;
  inset-block-end: 0;
  inset-inline-start: 0;
  cursor: pointer;
  padding: ${Spacings.small};
  z-index: ${LayoutIndexes.modal + 1};

  ${MediaQueries.desktop} {
    padding: ${Spacings.large};
  }

  > * {
    pointer-events: auto;
  }
`;
const SnackbarAnimationWrapper = ({ id, duration = 7000, closeOnClick, item, }) => {
    const [hideTimeout, setHideTimeout] = useState(duration);
    const [localShow, setLocalShow] = useState(true);
    const { isKeyboardControlled } = useInteractionMode();
    const isReducedMotion = useReducedMotion();
    const isDisabledMotion = isReducedMotion || isKeyboardControlled;
    useTimeout(() => {
        setLocalShow(false);
    }, hideTimeout);
    const handleMouseEnter = useCallback(() => {
        setHideTimeout(null);
    }, []);
    const handleMouseLeave = useCallback(() => {
        setHideTimeout(duration);
    }, [duration]);
    const handleClick = useCallback(() => {
        setHideTimeout(null);
        if (closeOnClick) {
            setLocalShow(false);
        }
    }, [closeOnClick]);
    const handleFocus = useCallback(() => {
        setHideTimeout(null);
    }, []);
    const handleBlur = useCallback(() => {
        setHideTimeout(duration);
    }, [duration]);
    const handleKeyDown = useCallback((e) => {
        // close the open snackbar
        if (e.key !== 'Tab' && e.key !== 'Meta' && e.key !== 'Shift') {
            setLocalShow(false);
        }
    }, []);
    return (React.createElement(AnimatePresence, null, localShow && (React.createElement(MotionWrapper, { key: `snackbar-region-${id}`, role: "alert", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, initial: {
            opacity: 0,
            height: 0,
            translateY: `0%`,
        }, animate: {
            opacity: 1,
            height: 'auto',
            translateY: `0%`,
        }, exit: {
            opacity: 0,
            height: 0,
            translateY: `-100%`,
        }, transition: createTransition({
            ...Transitions.default,
            disabled: isDisabledMotion,
        }) },
        React.createElement(WrappedSnackbar, { item: item, tabIndex: 0, onClick: handleClick, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown })))));
};
// let Snackbar itself determine how to manage cursor state:
const MotionWrapper = styled(motion.div) `
  cursor: default;

  &:hover {
    cursor: default;
  }
`;
const WrappedSnackbar = ({ item: { onClick: onItemClick, onFocus: onItemFocus, onBlur: onItemBlur, onKeyDown: onItemKeyDown, ...item }, onClick, onFocus, onBlur, onKeyDown, ...props }) => {
    const handleClick = useCallback((e) => {
        onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(e);
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [onItemClick, onClick]);
    const handleFocus = useCallback((e) => {
        onItemFocus === null || onItemFocus === void 0 ? void 0 : onItemFocus(e);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onItemFocus, onFocus]);
    const handleBlur = useCallback((e) => {
        onItemBlur === null || onItemBlur === void 0 ? void 0 : onItemBlur(e);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [onItemBlur, onBlur]);
    const handleKeyDown = useCallback((e) => {
        onItemKeyDown === null || onItemKeyDown === void 0 ? void 0 : onItemKeyDown(e);
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, [onItemKeyDown, onKeyDown]);
    return (React.createElement(Snackbar, { ...item, ...props, onClick: handleClick, onFocus: handleFocus, onKeyDown: handleKeyDown, onBlur: handleBlur }));
};
